import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { INVENTORY } from "@/constants/testIds";
import { PRODUCT_CATEGORIES, PRODUCT_CATEGORY_LABELS, UNIT_SUGGESTIONS } from "@/lib/inventoryConstants";

const EMPTY_FORM = { name: "", brand: "", category: "consumable", unit: "unidades", unit_cost: 0, stock_quantity: 0, min_stock: 0 };

/** Alta/edición de un producto del catalogo de inventario. El stock inicial
 * solo se define al crear; luego se modifica via compras o el ajuste manual. */
export default function ProductModal({ open, onOpenChange, product, onSubmit }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const isEdit = Boolean(product);

  useEffect(() => {
    if (open) {
      setForm(product ? {
        name: product.name || "",
        brand: product.brand || "",
        category: product.category || "consumable",
        unit: product.unit || "unidades",
        unit_cost: product.unit_cost ?? 0,
        stock_quantity: product.stock_quantity ?? 0,
        min_stock: product.min_stock ?? 0,
      } : EMPTY_FORM);
      setError("");
    }
  }, [product, open]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError("Ingresa el nombre del producto");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onSubmit({
        name: form.name.trim(),
        brand: form.brand.trim() || null,
        category: form.category,
        unit: form.unit.trim() || "unidades",
        unit_cost: Number(form.unit_cost) || 0,
        stock_quantity: Number(form.stock_quantity) || 0,
        min_stock: Number(form.min_stock) || 0,
      }, product);
      onOpenChange(false);
    } catch (err) {
      setError(err?.message || "No se pudo guardar el producto");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid={INVENTORY.productModal}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{isEdit ? "Editar producto" : "Nuevo producto"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Nombre</Label>
            <Input value={form.name} onChange={set("name")} data-testid={INVENTORY.productModalNameInput} placeholder="Ej: Botox Allergan" />
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Marca</Label>
            <Input value={form.brand} onChange={set("brand")} data-testid={INVENTORY.productModalBrandInput} placeholder="Ej: Allergan" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Categoría</Label>
              <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                <SelectTrigger data-testid={INVENTORY.productModalCategorySelect}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRODUCT_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{PRODUCT_CATEGORY_LABELS[c]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Unidad de medida</Label>
              <Input value={form.unit} onChange={set("unit")} list="inventory-unit-suggestions" data-testid={INVENTORY.productModalUnitInput} placeholder="unidades, ml..." />
              <datalist id="inventory-unit-suggestions">
                {UNIT_SUGGESTIONS.map((u) => <option key={u} value={u} />)}
              </datalist>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Costo unitario (USD)</Label>
              <Input type="number" min="0" step="0.01" value={form.unit_cost} onChange={set("unit_cost")} data-testid={INVENTORY.productModalCostInput} />
            </div>
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Stock mínimo</Label>
              <Input type="number" min="0" step="0.01" value={form.min_stock} onChange={set("min_stock")} data-testid={INVENTORY.productModalMinStockInput} />
            </div>
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">
              {isEdit ? "Stock actual" : "Stock inicial"}
            </Label>
            <Input
              type="number" min="0" step="0.01" value={form.stock_quantity} onChange={set("stock_quantity")}
              disabled={isEdit} data-testid={INVENTORY.productModalStockInput}
            />
            {isEdit && (
              <p className="text-xs text-muted-foreground mt-1">
                Para modificar el stock, registra una compra o usa "Ajustar stock".
              </p>
            )}
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid={INVENTORY.productModalCancelButton}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} data-testid={INVENTORY.productModalSaveButton}>
            {saving ? "Guardando..." : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
